import { createFileRoute, redirect } from '@tanstack/react-router';
import { getAuthenticatedSession, getDefaultExperience } from 'src/api/authentication';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { getSelectedSite, getSites } from 'src/api/admin/sites';
import { hardRedirect } from 'src/utils/hardRedirect';

export const Route = createFileRoute('/')({
	beforeLoad: async () => {
		const session = await getAuthenticatedSession();
		const experience = await getDefaultExperience();
		const is20Experience = experience === '2.0';
		// Not authenticated - redirect to the appropriate login page based on experience
		if (session?.user === undefined) {
			if (is20Experience) {
				await hardRedirect('/app/auth/');
			}
			throw redirect({ to: '/authentication/login' });
		}

		const verifyPermission = getVerifyPermission(session?.user?.permissions || []);
		const hasAdminAccess = verifyPermission(PERMISSIONS.adminView);

		// No admin access - check other permissions
		if (!hasAdminAccess) {
			if (verifyPermission(PERMISSIONS.companyView)) {
				throw redirect({ to: '/company' });
			} else if (verifyPermission(PERMISSIONS.personalView)) {
				throw redirect({ to: '/personal' });
			} else if (verifyPermission(PERMISSIONS.partnerCompanyView)) {
				// Redirect to partner portal
				throw redirect({ to: '/partner' });
			} else if (verifyPermission(PERMISSIONS.supervisorView)) {
				throw redirect({ to: '/supervisor' });
			} else {
				// Get selected site for the error message
				let selectedSiteId = '';
				try {
					const selectedSite = await getSelectedSite();
					selectedSiteId = selectedSite?.id || '';
				} catch {
					// Ignore error
				}
				throw redirect({
					to: '/403',
					search: {
						permissions: PERMISSIONS.adminView,
						occurredAt: `/${selectedSiteId}/overview`,
						method: 'GET',
					},
				});
			}
		}

		if (is20Experience) {
			await hardRedirect('/app/admin/');
		}

		if (!verifyPermission(PERMISSIONS.adminOverviewView)) {
			if (verifyPermission(PERMISSIONS.adminUsersView)) {
				throw redirect({ to: '/users' });
			} else if (verifyPermission(PERMISSIONS.adminGroupsView)) {
				throw redirect({ to: '/groups' });
			} else if (verifyPermission(PERMISSIONS.adminEventsView)) {
				throw redirect({ to: '/events' });
			} else {
				throw redirect({
					to: '/403',
					search: {
						permissions: 'NO_VIEW_PERMISSIONS_ADMIN_ENTITIES',
					},
				});
			}
		}

		// Get selected site and redirect to overview
		let selectedSite: { id: string } | null = null;
		try {
			selectedSite = await getSelectedSite();
		} catch {
			// API may fail if no site exists or preferences are invalid
		}
		if (!selectedSite) {
			const sitesRes = await getSites('noPagination=true');
			const sites = sitesRes?.data ?? [];
			if (sites.length > 0) {
				selectedSite = sites[0];
			}
		}
		if (!selectedSite?.id) {
			throw redirect({ to: '/system/sites' });
		}
		throw redirect({
			to: '/$siteId/overview',
			params: { siteId: selectedSite.id },
		});
	},
	component: Index,
});

function Index() {
	// This component should never render as the loader always redirects
	return null;
}
